import { eyeIcon, LoadIcon } from "@excalidraw/excalidraw/components/icons";
import { MainMenu } from "@excalidraw/excalidraw/index";
import { t } from "@excalidraw/excalidraw/i18n";
import React from "react";

import { isDevEnv } from "@excalidraw/common";

import type { Theme } from "@excalidraw/element/types";

import { LanguageList } from "../app-language/LanguageList";
import { isCloudEnabled } from "../cloud/config";
import { useCloud } from "../cloud/context";

import { saveDebugState } from "./DebugCanvas";

const CloudSaveIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
    <path d="M19.35 10.04A7.49 7.49 0 0 0 12 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 0 0 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z" />
  </svg>
);

export const AppMainMenu: React.FC<{
  onCollabDialogOpen: () => any;
  isCollaborating: boolean;
  isCollabEnabled: boolean;
  theme: Theme | "system";
  setTheme: (theme: Theme | "system") => void;
  refresh: () => void;
  onOpenLocal?: () => void;
}> = React.memo((props) => {
  const { user, currentKey, openSaveDialog } = useCloud();
  const cloudReady = isCloudEnabled() && !!user;

  return (
    <MainMenu>
      {cloudReady ? (
        // Custom "Open" that detaches the cloud binding first, so importing a
        // local file never overwrites the bound cloud drawing.
        <MainMenu.Item icon={LoadIcon} onSelect={() => props.onOpenLocal?.()}>
          {t("buttons.load")}
        </MainMenu.Item>
      ) : (
        <MainMenu.DefaultItems.LoadScene />
      )}
      <MainMenu.DefaultItems.SaveToActiveFile />
      {cloudReady && (
        <>
          <MainMenu.Item
            icon={<CloudSaveIcon />}
            onSelect={() => openSaveDialog(undefined)}
          >
            {currentKey ? `Save to Cloud` : "Save to Cloud…"}
          </MainMenu.Item>
          {currentKey && (
            <MainMenu.Item
              icon={<CloudSaveIcon />}
              onSelect={() => openSaveDialog(undefined)}
            >
              Save As… (Cloud)
            </MainMenu.Item>
          )}
        </>
      )}
      <MainMenu.DefaultItems.Export />
      <MainMenu.DefaultItems.SaveAsImage />
      {props.isCollabEnabled && (
        <MainMenu.DefaultItems.LiveCollaborationTrigger
          isCollaborating={props.isCollaborating}
          onSelect={() => props.onCollabDialogOpen()}
        />
      )}
      <MainMenu.DefaultItems.CommandPalette className="highlighted" />
      <MainMenu.DefaultItems.SearchMenu />
      <MainMenu.DefaultItems.Help />
      <MainMenu.DefaultItems.ClearCanvas />
      <MainMenu.Separator />
      {isDevEnv() && (
        <MainMenu.Item
          icon={eyeIcon}
          onSelect={() => {
            if (window.visualDebug) {
              delete window.visualDebug;
              saveDebugState({ enabled: false });
            } else {
              window.visualDebug = { data: [] };
              saveDebugState({ enabled: true });
            }
            props?.refresh();
          }}
        >
          Visual Debug
        </MainMenu.Item>
      )}
      <MainMenu.Separator />
      <MainMenu.DefaultItems.Preferences />
      <MainMenu.DefaultItems.ToggleTheme
        allowSystemTheme
        theme={props.theme}
        onSelect={props.setTheme}
      />
      <MainMenu.ItemCustom>
        <LanguageList style={{ width: "100%" }} />
      </MainMenu.ItemCustom>
      <MainMenu.DefaultItems.ChangeCanvasBackground />
    </MainMenu>
  );
});
